// DuckDB-WASM統合
//
// NOTE: DuckDBのテストについて
// DuckDB-WASMは初期化時にURL.createObjectURL()とWorkerを使用するため、
// ブラウザ環境でのみ動作します。そのため、単体テスト・統合テスト（Node.js環境）では
// テストできません。DuckDBの動作は以下のE2Eテストでカバーされています：
// - tests/e2e/web/dataset-reload-web.spec.ts: "Reload with derived dataset recalculation"

import * as duckdb from '@duckdb/duckdb-wasm';
import * as arrow from 'apache-arrow';
// Worker.jsはローカルバンドル（Firefoxの同一オリジン制限のため）
import duckdb_worker_mvp from '@duckdb/duckdb-wasm/dist/duckdb-browser-mvp.worker.js?url';
import duckdb_worker_eh from '@duckdb/duckdb-wasm/dist/duckdb-browser-eh.worker.js?url';
import type { DataSet } from '../../../types/dataset.types';
import type { OperationResult } from '../executeOperation';
import { executeOperationWithoutLock, type SqlExecutor } from '../executeOperation';
import type { DataSchema, ColumnSchema, DataType, DataValue } from '../../../types/core.types';
import { asISODateTimeString } from '../../../types/core.types';
import type { DatasetId } from '../../../types';
import { createColumnId, createColumnName } from '../../../types';
import { ROW_ID_COLUMN_ID, ROW_ID_COLUMN_NAME } from '../../../types/system-columns';
import logger from '../logger';
import { topologicalSort } from '../topologicalSort';

/**
 * DuckDB-WASMの詳細ログを取得するカスタムロガー
 *
 * DuckDB-WASMは内部で様々なログを出力するが、デフォルトのConsoleLoggerでは
 * 情報が不足する場合がある。このロガーは全てのログレベルを捕捉し、
 * MIDASのロギングシステムに統合する。
 *
 * 特に以下の情報を取得するために使用：
 * - WASMバイナリのダウンロード進捗
 * - WASMコンパイル状況
 * - Worker通信の状態
 * - 初期化中のエラー詳細
 *
 * LogEntryVariantの型定義（DuckDB-WASMより）：
 * - origin: BINDINGS | ASYNC_DUCKDB
 * - topic: INSTANTIATE | QUERY | CONNECT | DISCONNECT | OPEN
 * - event: ERROR | START | OK | RUN
 * - value: string | void
 */
class DuckDBDiagnosticLogger implements duckdb.Logger {
  private logCount = 0;

  log(entry: duckdb.LogEntryVariant): void {
    this.logCount++;

    // LogEntryVariantから情報を抽出
    // 各フィールドはenum値なので、文字列に変換
    const logData = {
      level: duckdb.LogLevel[entry.level],
      origin: duckdb.LogOrigin[entry.origin],
      topic: duckdb.LogTopic[entry.topic],
      event: duckdb.LogEvent[entry.event],
      value: entry.value,
      entryTimestamp: entry.timestamp?.toISOString(),
      captureTimestamp: new Date().toISOString(),
      logIndex: this.logCount
    };

    // ログレベルに応じてMIDASのロガーを使い分け
    switch (entry.level) {
      case duckdb.LogLevel.ERROR:
        logger.error('[DuckDB-WASM] Internal error', logData);
        break;
      case duckdb.LogLevel.WARNING:
        logger.warn('[DuckDB-WASM] Internal warning', logData);
        break;
      case duckdb.LogLevel.INFO:
        logger.info('[DuckDB-WASM] Internal info', logData);
        break;
      default:
        logger.debug('[DuckDB-WASM] Internal debug', logData);
    }
  }
}

/**
 * DuckDB AST構造の型定義
 */
interface DuckDBASTResponse {
  error: boolean;
  statements: DuckDBStatement[];
}

interface DuckDBStatement {
  node: QueryNode;
  named_param_map?: unknown[];
}

/**
 * クエリノードの型（SELECT_NODEまたはSET_OPERATION_NODE）
 */
type QueryNode = SelectNode | SetOperationNode;

interface SelectNode {
  type: 'SELECT_NODE';
  from_table?: FromTable;
  cte_map?: {
    map: Array<{ key: string; value: CTEEntry }>;
  };
  select_list?: Expression[];
  where_clause?: Expression;
  having?: Expression;
}

/**
 * SET_OPERATION_NODE: UNION, INTERSECT, EXCEPT
 */
interface SetOperationNode {
  type: 'SET_OPERATION_NODE';
  setop_type: 'UNION' | 'INTERSECT' | 'EXCEPT' | 'UNION_BY_NAME';
  left: QueryNode;
  right: QueryNode;
  cte_map?: {
    map: Array<{ key: string; value: CTEEntry }>;
  };
}

/**
 * DuckDB AST式ノードの型定義
 * 式は様々な型があり、サブクエリを含む場合は class: "SUBQUERY" となる
 */
interface Expression {
  class: string; // "SUBQUERY", "COMPARISON", "FUNCTION", "COLUMN_REF", etc.
  type: string;
  alias?: string;
  // サブクエリの場合
  subquery_type?: string; // "ANY", "EXISTS", "SCALAR"
  subquery?: {
    node: QueryNode;
  };
  // 比較演算子の場合
  left?: Expression;
  right?: Expression;
  // 関数の場合
  children?: Expression[];
  filter?: Expression;
  // IN/ANY の child
  child?: Expression;
}

interface CTEEntry {
  query: {
    node: QueryNode;
  };
  aliases?: unknown[];
  materialized?: unknown;
}

interface FromTable {
  type: string; // "BASE_TABLE", "JOIN", "SUBQUERY" など
  table_name?: string; // BASE_TABLEの場合
  schema_name?: string;
  catalog_name?: string;
  alias?: string;
  query_location?: number; // SQL文内の開始位置（0-indexed）
  // JOINの場合
  left?: FromTable;
  right?: FromTable;
  // サブクエリの場合（FROM句のderived table）
  subquery?: {
    node: QueryNode;
  };
}

/**
 * SQL文内のテーブル参照情報
 * query_locationはSQL文内でテーブル名（ダブルクォート含む）が開始する位置（0-indexed）
 */
export interface TableReference {
  tableName: string;
  queryLocation: number;
}

export class DuckDBQueryEngine {
  private db: duckdb.AsyncDuckDB | null = null;
  private conn: duckdb.AsyncDuckDBConnection | null = null;

  /**
   * DuckDBへの書き込み操作を直列化するためのPromiseチェーン
   *
   * ## 原理
   * lockChainは常に「実行チェーンの最後」を指す未解決のPromiseを保持する。
   * 新しい処理は以下の手順で直列化される：
   *
   * 1. 現在のlockChain（前の処理）を保存
   * 2. 自分の処理用の新しいPromiseを作成し、lockChainを置き換える
   * 3. 保存した前の処理の完了を待つ（await previousLock）
   * 4. 自分の処理を実行
   * 5. 完了したらresolveして、次の処理に通知
   *
   * ## なぜ安全か
   * JavaScriptの代入演算はアトミック（原子的）で、await到達までは他の処理が割り込めない。
   * そのため、以下の2行は常に連続して実行される：
   *
   *   const previousLock = this.lockChain;  // スナップショット取得
   *   this.lockChain = new Promise(...);    // チェーンの最後を更新
   *
   * これにより、どのタイミングで処理が呼ばれても、確実に前の処理の完了を待つ。
   *
   * ## 実行例
   * ```
   * 時刻  lockChain           処理A                    処理B
   * ─────────────────────────────────────────────────────────
   * t0   Promise(完了済み)
   *
   * t1                      withLock(A)開始
   *                         previousLock = 完了済み
   *                         lockChain = Promise(未解決A)
   *
   * t2                      await完了 → 即通過
   *                         処理A実行中...
   *
   * t3   Promise(未解決A)                           withLock(B)開始
   *                                                 previousLock = 未解決A
   *                                                 lockChain = Promise(未解決B)
   *
   * t4   Promise(未解決B)                           await 未解決A
   *                                                 ↓ ブロック（待機中）
   *
   * t5                      処理A完了
   *                         releaseLock!()
   *                         → Promise(未解決A)が解決
   *
   * t6                                               await完了！
   *                                                 処理B実行中...
   *
   * t7                                               処理B完了
   *                                                 releaseLock!()
   *                                                 → Promise(未解決B)が解決
   * ```
   */
  private lockChain: Promise<void> = Promise.resolve();

  /**
   * DuckDB-WASMを初期化
   *
   * ## 初期化プロセスの詳細
   *
   * 1. selectBundle: ブラウザの機能を検出し、最適なWASMバンドルを選択
   *    - eh (Exception Handling): モダンブラウザ向け、高速
   *    - mvp (Minimum Viable Product): 古いブラウザ向け、互換性重視
   *
   * 2. Worker creation: Web Workerを作成してDuckDBを別スレッドで実行
   *
   * 3. instantiate: WASMバイナリをダウンロード・コンパイル
   *    - CDNからWASMファイル（32-37MB）をダウンロード
   *    - ブラウザがWASMをコンパイル（CPUバウンド、数秒〜数十秒）
   *    - HTTPキャッシュがない場合、ネットワーク遅延が加算される
   *
   * 4. connect: データベース接続を確立
   *
   * ## パフォーマンス特性
   *
   * - 初回: WASMダウンロード + コンパイル（10-60秒）
   * - キャッシュあり: コンパイル済みWASMをロード（1-5秒）
   * - Firefoxは他ブラウザよりWASMコンパイルが遅い傾向
   */
  async init() {
    // 既に初期化済みの場合はスキップ
    if (this.db && this.conn) {
      logger.debug('[DuckDB] Already initialized, skipping');
      return;
    }

    const initStart = performance.now();

    // 環境情報を詳細に記録（問題発生時の調査用）
    const envInfo = {
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A',
      platform: typeof navigator !== 'undefined' ? navigator.platform : 'N/A',
      hardwareConcurrency: typeof navigator !== 'undefined' ? navigator.hardwareConcurrency : 'N/A',
      deviceMemory: typeof navigator !== 'undefined' ? (navigator as Navigator & { deviceMemory?: number }).deviceMemory : 'N/A',
      connection: typeof navigator !== 'undefined' && 'connection' in navigator
        ? {
            effectiveType: (navigator as Navigator & { connection?: { effectiveType?: string; downlink?: number; rtt?: number } }).connection?.effectiveType,
            downlink: (navigator as Navigator & { connection?: { effectiveType?: string; downlink?: number; rtt?: number } }).connection?.downlink,
            rtt: (navigator as Navigator & { connection?: { effectiveType?: string; downlink?: number; rtt?: number } }).connection?.rtt
          }
        : 'N/A',
      timestamp: new Date().toISOString()
    };

    logger.info('[DuckDB] === INITIALIZATION START ===', envInfo);

    try {
      // WASMファイルはCDNから、Worker.jsファイルはローカルバンドルから
      // （FirefoxのWorker同一オリジン制限のため）
      const CDN_BASE_URL = 'https://cdn.jsdelivr.net/npm/@duckdb/duckdb-wasm@1.30.0/dist';

      const MANUAL_BUNDLES: duckdb.DuckDBBundles = {
        mvp: {
          mainModule: `${CDN_BASE_URL}/duckdb-mvp.wasm`,  // CDNから（37MB）
          mainWorker: duckdb_worker_mvp,                  // ローカル（800KB）
        },
        eh: {
          mainModule: `${CDN_BASE_URL}/duckdb-eh.wasm`,   // CDNから（32MB）
          mainWorker: duckdb_worker_eh,                   // ローカル（800KB）
        },
      };

      logger.info('[DuckDB] Step 1/4: selectBundle starting', {
        mvpModule: MANUAL_BUNDLES.mvp?.mainModule,
        ehModule: MANUAL_BUNDLES.eh?.mainModule
      });

      const t0 = performance.now();
      const bundle = await duckdb.selectBundle(MANUAL_BUNDLES);
      const selectBundleTime = performance.now() - t0;
      logger.info('[DuckDB] Step 1/4: selectBundle completed', {
        ms: selectBundleTime.toFixed(2),
        selectedBundle: bundle.mainModule?.includes('eh') ? 'eh' : 'mvp',
        mainModule: bundle.mainModule,
        hasPthreadWorker: !!bundle.pthreadWorker
      });

      // Workerを直接構築（importScriptsではなく）
      if (!bundle.mainWorker) {
        throw new Error('Failed to select DuckDB bundle: mainWorker is null');
      }
      if (!bundle.mainModule) {
        throw new Error('Failed to select DuckDB bundle: mainModule is null');
      }

      logger.info('[DuckDB] Step 2/4: Worker creation starting', {
        workerUrl: bundle.mainWorker
      });

      const t1 = performance.now();
      const worker = new Worker(bundle.mainWorker);

      // Workerエラーハンドラを設定（初期化中のエラー捕捉用）
      worker.onerror = (event) => {
        logger.error('[DuckDB] Worker error during initialization', {
          message: event.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          elapsedMs: (performance.now() - initStart).toFixed(2)
        });
      };

      // カスタム診断ロガーを使用
      const duckLogger = new DuckDBDiagnosticLogger();
      this.db = new duckdb.AsyncDuckDB(duckLogger, worker);
      const workerCreationTime = performance.now() - t1;
      logger.info('[DuckDB] Step 2/4: Worker creation completed', {
        ms: workerCreationTime.toFixed(2)
      });

      logger.info('[DuckDB] Step 3/4: instantiate starting (WASM download + compile)', {
        wasmUrl: bundle.mainModule,
        note: 'This step can take 10-60 seconds on first load or cache miss'
      });

      const t2 = performance.now();

      // instantiateの進捗を監視するためのタイマー
      const progressInterval = setInterval(() => {
        const elapsed = performance.now() - t2;
        logger.info('[DuckDB] Step 3/4: instantiate in progress...', {
          elapsedMs: elapsed.toFixed(2),
          elapsedSec: (elapsed / 1000).toFixed(1)
        });
      }, 5000); // 5秒ごとに進捗ログ

      try {
        await this.db.instantiate(bundle.mainModule, bundle.pthreadWorker);
      } finally {
        clearInterval(progressInterval);
      }

      const instantiateTime = performance.now() - t2;
      logger.info('[DuckDB] Step 3/4: instantiate completed', {
        ms: instantiateTime.toFixed(2),
        seconds: (instantiateTime / 1000).toFixed(2),
        note: instantiateTime > 10000 ? 'SLOW: Likely cache miss or slow network' : 'Normal'
      });

      logger.info('[DuckDB] Step 4/4: connect starting');

      const t3 = performance.now();
      this.conn = await this.db.connect();
      const connectTime = performance.now() - t3;
      logger.info('[DuckDB] Step 4/4: connect completed', {
        ms: connectTime.toFixed(2)
      });

      const totalTime = performance.now() - initStart;
      logger.info('[DuckDB] === INITIALIZATION COMPLETE ===', {
        totalMs: totalTime.toFixed(2),
        totalSeconds: (totalTime / 1000).toFixed(2),
        breakdown: {
          selectBundle: selectBundleTime.toFixed(2),
          workerCreation: workerCreationTime.toFixed(2),
          instantiate: instantiateTime.toFixed(2),
          connect: connectTime.toFixed(2)
        },
        performance: totalTime > 30000 ? 'SLOW' : totalTime > 10000 ? 'MODERATE' : 'FAST'
      });
    } catch (error) {
      const elapsedMs = performance.now() - initStart;
      logger.error('[DuckDB] === INITIALIZATION FAILED ===', {
        error: error instanceof Error ? {
          name: error.name,
          message: error.message,
          stack: error.stack
        } : String(error),
        elapsedMs: elapsedMs.toFixed(2),
        elapsedSeconds: (elapsedMs / 1000).toFixed(2),
        environment: envInfo
      });
      throw new Error(`DuckDB initialization failed after ${(elapsedMs / 1000).toFixed(1)}s: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Connectionをクローズ（プロジェクトを閉じる時に呼ぶ）
   */
  async close() {
    if (this.conn) {
      await this.conn.close();
      this.conn = null;
    }
    if (this.db) {
      await this.db.terminate();
      this.db = null;
    }
    logger.info('[DuckDB] Connection closed');
  }

  /**
   * 現在ロックが保持されているかを示すフラグ
   */
  private isLockHeld: boolean = false;


  /**
   * ロックが現在保持されているかを確認
   * @returns ロックが保持されている場合true
   */
  isLocked(): boolean {
    return this.isLockHeld;
  }

  /**
   * DuckDBへの書き込み処理を排他制御するヘルパーメソッド
   *
   * @param fn 実行する非同期処理
   * @returns 処理の結果
   *
   * ## 使い方
   * ```typescript
   * await duckdbEngine.withLock(async () => {
   *   // テーブル登録などの書き込み処理
   *   await this.conn.query('DROP TABLE ...');
   *   await this.conn.insertArrowTable(...);
   * });
   * ```
   */
  async withLock<T>(fn: () => Promise<T>): Promise<T> {
    // ① 現在のlockChain（前の処理）を保存
    // この時点でスナップショットを取ることで、確実に前の処理を捕捉する
    const previousLock = this.lockChain;

    // ② 自分の処理用の新しいPromiseを作成し、lockChainを置き換える
    // これにより、この処理が「チェーンの最後」になる
    let releaseLock: () => void;
    this.lockChain = new Promise<void>((resolve) => {
      releaseLock = resolve;
    });

    // ③ 前の処理（保存したpreviousLock）の完了を待つ
    // ここで初めてawaitするので、他の処理が実行される可能性がある
    // しかし、①②は既に完了しているので、チェーンは正しく構築されている
    await previousLock;

    // ④ 自分の処理を実行
    try {
      this.isLockHeld = true;
      return await fn();
    } finally {
      // ⑤ 完了したらresolveして、次の処理（this.lockChainを待っている処理）に通知
      this.isLockHeld = false;
      releaseLock!();
    }
  }

  /**
   * 全テーブルを削除（データセットリロード時に使用）
   */
  async clearAllTables() {
    if (!this.conn) {
      logger.warn('[DuckDB] Cannot clear tables - not initialized');
      return;
    }

    return this.withLock(async () => {
      // mainスキーマの全テーブルを取得
      const result = await this.conn!.query(
        "SELECT table_name FROM information_schema.tables WHERE table_schema = 'main'"
      );
      const tables = result.toArray().map((row) => (row as { table_name: string }).table_name);

      // 各テーブルを削除
      for (const tableName of tables) {
        const quotedName = this.quoteTableName(tableName);
        await this.conn!.query(`DROP TABLE IF EXISTS ${quotedName}`);
        logger.debug('[DuckDB] Dropped table', { tableName });
      }

      logger.info('[DuckDB] All tables cleared', { count: tables.length });
    });
  }

  /**
   * データセットをDuckDBテーブルとして登録（Internal版、ロック取得済み前提）
   *
   * このメソッドはロックが既に取得されていることを前提としています。
   * evaluateDerivedDatasetから呼び出す場合に使用します。
   */
  async registerDatasetWithoutLock(dataset: DataSet, skipIfExists: boolean = false): Promise<void> {
    logger.assert(this.isLocked(), "Lock must be acquired before calling registerDatasetWithoutLock");

    // データが無いデータセットは登録できない
    if (!dataset.data) {
      logger.debug('[DuckDB] Skipping registration - no data', { name: dataset.name });
      throw new Error(`Cannot register dataset without data: ${dataset.name}`);
    }

    const regPerfStart = performance.now();

    // skipIfExistsが有効で、テーブルが既に存在する場合はスキップ
    if (skipIfExists) {
      try {
        // 既存テーブルの行数を確認（クエリ実行時にはクォートが必要）
        const quotedName = this.quoteTableName(dataset.name);
        const countResult = await this.conn!.query(`SELECT COUNT(*) as count FROM ${quotedName}`);
        const existingCount = countResult.toArray()[0]?.count || 0;

        // 行数が同じ場合はスキップ（データが変わっていないと仮定）
        if (existingCount === dataset.rowCount) {
          logger.info('[DuckDB] ✓ Skipped duplicate registration', {
            name: dataset.name,
            rowCount: existingCount,
            message: 'Table already exists with same row count'
          });
          return;
        } else {
          logger.info('[DuckDB] Updating table with different row count', {
            name: dataset.name,
            oldRowCount: existingCount,
            newRowCount: dataset.rowCount
          });
        }
      } catch {
        // テーブルが存在しない場合は続行
      }
    }

    // 既存テーブルを削除（存在しなくてもエラーにならない）
    const dropStart = performance.now();
    const quotedName = this.quoteTableName(dataset.name);
    await this.conn!.query(`DROP TABLE IF EXISTS ${quotedName}`);
    const dropTime = performance.now() - dropStart;

    // 新しいテーブルを作成
    const arrowStart = performance.now();
    const arrowTable = this.datasetToArrow(dataset);
    const arrowTime = performance.now() - arrowStart;

    const insertStart = performance.now();
    await this.conn!.insertArrowTable(arrowTable, { name: dataset.name });
    const insertTime = performance.now() - insertStart;

    const regTotalTime = performance.now() - regPerfStart;
    logger.info('[DuckDB] ⏱️ registerDataset breakdown', {
      dataset: dataset.name,
      drop: dropTime.toFixed(2),
      arrowConversion: arrowTime.toFixed(2),
      insert: insertTime.toFixed(2),
      total: regTotalTime.toFixed(2)
    });

    logger.debug('[DuckDB] Registered/Updated dataset', {
      name: dataset.name,
      rows: dataset.rowCount,
      columns: dataset.schema.columns.map(c => c.name)
    });
  }


  /**
   * デバッグ用：現在登録されているテーブルの内容を取得
   */
  async debugGetTableData(tableName: string): Promise<unknown[] | null> {
    if (!this.conn) {
      throw new Error('DuckDB not initialized');
    }

    const normalizedName = this.quoteTableName(tableName);
    try {
      const result = await this.conn.query(`SELECT * FROM ${normalizedName}`);
      return result.toArray();
    } catch (error) {
      logger.error('[DuckDB] Failed to get table data', { tableName: normalizedName, error });
      return null;
    }
  }

  /**
   * デバッグ用：現在登録されているテーブル一覧を取得
   */
  async debugGetTables(): Promise<string[]> {
    if (!this.conn) {
      throw new Error('DuckDB not initialized');
    }

    try {
      const result = await this.conn.query("SELECT table_name FROM information_schema.tables WHERE table_schema = 'main'");
      return result.toArray().map((row) => (row as { table_name: string }).table_name);
    } catch (error) {
      logger.error('[DuckDB] Failed to get table list', { error });
      return [];
    }
  }

  /**
   * SQLクエリから参照テーブル名を抽出
   * json_serialize_sqlを使用してASTを解析
   *
   * このメソッドは、テーブルが存在しなくてもクエリから参照テーブル名を抽出できます。
   * これにより、クエリ実行前に必要なテーブルのみを登録する最適化が可能になります。
   *
   * @param query SQLクエリ
   * @returns 参照されているテーブル名の配列
   */
  async extractTableNamesFromQuery(query: string): Promise<string[]> {
    await this.init();

    if (!this.conn) {
      throw new Error('DuckDB not initialized');
    }

    try {
      // json_serialize_sqlでASTを取得
      // テーブルが存在しなくても構文解析は成功する
      const result = await this.conn.query(
        `SELECT json_serialize_sql(${this.escapeSQLString(query)}) as ast`
      );
      const rows = result.toArray() as Array<{ ast: string }>;

      if (rows.length === 0 || !rows[0].ast) {
        logger.warn('[DuckDB] No AST output from json_serialize_sql');
        return [];
      }

      // ASTをパース
      const ast = JSON.parse(rows[0].ast) as DuckDBASTResponse;

      if (ast.error) {
        logger.warn('[DuckDB] AST contains error', { query });
        return [];
      }

      // ASTからテーブル名を抽出
      const tableNames = this.extractTableNamesFromAST(ast);

      logger.debug('[DuckDB] Extracted table names from AST', {
        tableNames,
        query: query.substring(0, 100)
      });

      return tableNames;
    } catch (error) {
      // 構文エラーなどでjson_serialize_sqlが失敗した場合
      logger.warn('[DuckDB] Could not extract table names from query', {
        message: error instanceof Error ? error.message : String(error),
        query: query.substring(0, 100)
      });
      return [];
    }
  }

  /**
   * SQLクエリから参照されているテーブル名と位置情報を抽出
   *
   * @param query SQLクエリ
   * @returns テーブル参照情報の配列（テーブル名とSQL文内の開始位置）
   */
  async extractTableReferencesWithLocation(query: string): Promise<TableReference[]> {
    await this.init();

    if (!this.conn) {
      throw new Error('DuckDB not initialized');
    }

    try {
      const result = await this.conn.query(
        `SELECT json_serialize_sql(${this.escapeSQLString(query)}) as ast`
      );
      const rows = result.toArray() as Array<{ ast: string }>;

      if (rows.length === 0 || !rows[0].ast) {
        logger.warn('[DuckDB] No AST output from json_serialize_sql');
        return [];
      }

      const ast = JSON.parse(rows[0].ast) as DuckDBASTResponse;

      if (ast.error) {
        logger.warn('[DuckDB] AST contains error', { query });
        return [];
      }

      // ASTからテーブル参照（位置情報付き）を抽出
      const tableRefs = this.extractTableRefsWithLocationFromAST(ast);

      // バイト位置 vs 文字位置の調査用ログ
      // UTF-8バイト位置を計算して比較
      const encoder = new TextEncoder();
      const bytePositions: number[] = [];
      let byteOffset = 0;
      for (let i = 0; i < query.length; i++) {
        bytePositions.push(byteOffset);
        byteOffset += encoder.encode(query[i]).length;
      }

      logger.info('[DuckDB] query_location investigation', {
        query: query.substring(0, 150),
        queryLengthChars: query.length,
        queryLengthBytes: encoder.encode(query).length,
        tableRefs: tableRefs.map(ref => {
          const charPos = query.indexOf(`"${ref.tableName}"`);
          const bytePos = charPos >= 0 ? bytePositions[charPos] : -1;
          return {
            tableName: ref.tableName,
            queryLocation: ref.queryLocation,
            expectedCharPos: charPos,
            expectedBytePos: bytePos,
            matchesCharPos: ref.queryLocation === charPos,
            matchesBytePos: ref.queryLocation === bytePos
          };
        })
      });

      logger.debug('[DuckDB] Extracted table references with location', {
        tableRefs,
        query: query.substring(0, 100)
      });

      return tableRefs;
    } catch (error) {
      logger.warn('[DuckDB] Could not extract table references from query', {
        message: error instanceof Error ? error.message : String(error),
        query: query.substring(0, 100)
      });
      return [];
    }
  }

  /**
   * DuckDB ASTからテーブル参照（位置情報付き）を再帰的に抽出
   */
  private extractTableRefsWithLocationFromAST(ast: DuckDBASTResponse): TableReference[] {
    const tableRefs: TableReference[] = [];

    for (const statement of ast.statements) {
      const cteNames = new Set<string>();
      if (statement.node.cte_map && statement.node.cte_map.map) {
        for (const cte of statement.node.cte_map.map) {
          cteNames.add(cte.key);
        }
      }

      this.extractTableRefsFromQueryNode(statement.node, tableRefs, cteNames);
    }

    return tableRefs;
  }

  /**
   * QueryNodeからテーブル参照（位置情報付き）を再帰的に抽出
   */
  private extractTableRefsFromQueryNode(
    node: QueryNode,
    tableRefs: TableReference[],
    cteNames: Set<string>
  ): void {
    // CTE定義内のテーブル参照を抽出
    if (node.cte_map && node.cte_map.map) {
      for (const cte of node.cte_map.map) {
        this.extractTableRefsFromQueryNode(cte.value.query.node, tableRefs, cteNames);
      }
    }

    if (node.type === 'SET_OPERATION_NODE') {
      this.extractTableRefsFromQueryNode(node.left, tableRefs, cteNames);
      this.extractTableRefsFromQueryNode(node.right, tableRefs, cteNames);
    } else {
      this.extractTableRefsFromSelectNode(node, tableRefs, cteNames);
    }
  }

  /**
   * SelectNodeからテーブル参照（位置情報付き）を再帰的に抽出
   */
  private extractTableRefsFromSelectNode(
    node: SelectNode,
    tableRefs: TableReference[],
    cteNames: Set<string>
  ): void {
    if (node.from_table) {
      this.extractTableRefsFromFromTable(node.from_table, tableRefs, cteNames);
    }

    // サブクエリ内のテーブル参照も抽出
    if (node.select_list) {
      for (const expr of node.select_list) {
        this.extractTableRefsFromExpression(expr, tableRefs, cteNames);
      }
    }

    if (node.where_clause) {
      this.extractTableRefsFromExpression(node.where_clause, tableRefs, cteNames);
    }

    if (node.having) {
      this.extractTableRefsFromExpression(node.having, tableRefs, cteNames);
    }
  }

  /**
   * FromTableからテーブル参照（位置情報付き）を再帰的に抽出
   */
  private extractTableRefsFromFromTable(
    fromTable: FromTable,
    tableRefs: TableReference[],
    cteNames: Set<string>
  ): void {
    switch (fromTable.type) {
      case 'BASE_TABLE':
        if (fromTable.table_name && fromTable.query_location !== undefined) {
          // CTE名は除外
          if (!cteNames.has(fromTable.table_name)) {
            tableRefs.push({
              tableName: fromTable.table_name,
              queryLocation: fromTable.query_location
            });
          }
        }
        break;

      case 'JOIN':
      case 'CROSS_PRODUCT':
        if (fromTable.left) {
          this.extractTableRefsFromFromTable(fromTable.left, tableRefs, cteNames);
        }
        if (fromTable.right) {
          this.extractTableRefsFromFromTable(fromTable.right, tableRefs, cteNames);
        }
        break;

      case 'SUBQUERY':
        if (fromTable.subquery?.node) {
          this.extractTableRefsFromQueryNode(fromTable.subquery.node, tableRefs, cteNames);
        }
        break;
    }
  }

  /**
   * 式ノードからテーブル参照（位置情報付き）を再帰的に抽出
   */
  private extractTableRefsFromExpression(
    expr: Expression,
    tableRefs: TableReference[],
    cteNames: Set<string>
  ): void {
    if (!expr) return;

    if (expr.class === 'SUBQUERY' && expr.subquery?.node) {
      this.extractTableRefsFromQueryNode(expr.subquery.node, tableRefs, cteNames);
    }

    if (expr.left) {
      this.extractTableRefsFromExpression(expr.left, tableRefs, cteNames);
    }
    if (expr.right) {
      this.extractTableRefsFromExpression(expr.right, tableRefs, cteNames);
    }
    if (expr.children) {
      for (const child of expr.children) {
        this.extractTableRefsFromExpression(child, tableRefs, cteNames);
      }
    }
    if (expr.filter) {
      this.extractTableRefsFromExpression(expr.filter, tableRefs, cteNames);
    }
    if (expr.child) {
      this.extractTableRefsFromExpression(expr.child, tableRefs, cteNames);
    }
  }

  /**
   * SQL文字列をエスケープしてSELECT文に埋め込めるようにする
   *
   * @param sql エスケープするSQL文字列
   * @returns エスケープ済みのSQL文字列（シングルクォートで囲まれた状態）
   */
  private escapeSQLString(sql: string): string {
    // シングルクォートを2つ重ねてエスケープし、全体をシングルクォートで囲む
    return `'${sql.replace(/'/g, "''")}'`;
  }

  /**
   * DuckDB ASTからテーブル名を再帰的に抽出
   *
   * @param ast json_serialize_sqlから取得したAST
   * @returns 参照されているテーブル名の配列
   */
  private extractTableNamesFromAST(ast: DuckDBASTResponse): string[] {
    const tableNames = new Set<string>();

    // 全てのステートメントを処理
    for (const statement of ast.statements) {
      // CTE名を収集（これらは実テーブルではないので除外する必要がある）
      const cteNames = new Set<string>();
      if (statement.node.cte_map && statement.node.cte_map.map) {
        for (const cte of statement.node.cte_map.map) {
          cteNames.add(cte.key);
        }
      }

      this.extractTablesFromQueryNode(statement.node, tableNames, cteNames);
    }

    const result = Array.from(tableNames);
    logger.debug('[DuckDB] Final extracted table names', { tableNames: result });
    return result;
  }

  /**
   * QueryNode（SELECT_NODEまたはSET_OPERATION_NODE）からテーブル名を再帰的に抽出
   *
   * @param node QueryNode
   * @param tableNames 抽出されたテーブル名の集合
   * @param cteNames CTE名の集合（これらは実テーブルではないので除外する）
   */
  private extractTablesFromQueryNode(
    node: QueryNode,
    tableNames: Set<string>,
    cteNames: Set<string> = new Set()
  ): void {
    // CTE (Common Table Expression / WITH句) を処理
    if (node.cte_map && node.cte_map.map) {
      for (const cte of node.cte_map.map) {
        // CTE定義内のテーブル参照を抽出
        this.extractTablesFromQueryNode(cte.value.query.node, tableNames, cteNames);
      }
    }

    if (node.type === 'SET_OPERATION_NODE') {
      // UNION/INTERSECT/EXCEPT: 左右のクエリを再帰的に処理
      this.extractTablesFromQueryNode(node.left, tableNames, cteNames);
      this.extractTablesFromQueryNode(node.right, tableNames, cteNames);
    } else {
      // SELECT_NODE
      this.extractTablesFromSelectNode(node, tableNames, cteNames);
    }
  }

  /**
   * SelectNodeからテーブル名を再帰的に抽出
   *
   * @param node SelectNode
   * @param tableNames 抽出されたテーブル名の集合
   * @param cteNames CTE名の集合（これらは実テーブルではないので除外する）
   */
  private extractTablesFromSelectNode(
    node: SelectNode,
    tableNames: Set<string>,
    cteNames: Set<string>
  ): void {
    // FROM句を処理
    if (node.from_table) {
      this.extractTablesFromFromTable(node.from_table, tableNames, cteNames);
    }

    // SELECT句の式リストを処理（スカラーサブクエリ対応）
    if (node.select_list) {
      for (const expr of node.select_list) {
        this.extractTablesFromExpression(expr, tableNames, cteNames);
      }
    }

    // WHERE句を処理（IN, EXISTS, 比較サブクエリ対応）
    if (node.where_clause) {
      this.extractTablesFromExpression(node.where_clause, tableNames, cteNames);
    }

    // HAVING句を処理
    if (node.having) {
      this.extractTablesFromExpression(node.having, tableNames, cteNames);
    }
  }

  /**
   * 式ノードからテーブル名を再帰的に抽出
   * サブクエリを含む式を探索し、テーブル参照を抽出する
   *
   * @param expr 式ノード
   * @param tableNames 抽出されたテーブル名の集合
   * @param cteNames CTE名の集合
   */
  private extractTablesFromExpression(
    expr: Expression,
    tableNames: Set<string>,
    cteNames: Set<string>
  ): void {
    if (!expr) return;

    // サブクエリ式の場合、内部のQueryNodeを処理
    if (expr.class === 'SUBQUERY' && expr.subquery?.node) {
      logger.debug('[DuckDB] Found subquery in expression', {
        subquery_type: expr.subquery_type
      });
      this.extractTablesFromQueryNode(expr.subquery.node, tableNames, cteNames);
    }

    // 比較演算子の左右を再帰的に処理
    if (expr.left) {
      this.extractTablesFromExpression(expr.left, tableNames, cteNames);
    }
    if (expr.right) {
      this.extractTablesFromExpression(expr.right, tableNames, cteNames);
    }

    // 関数の子要素を処理
    if (expr.children) {
      for (const child of expr.children) {
        this.extractTablesFromExpression(child, tableNames, cteNames);
      }
    }

    // フィルタ式を処理
    if (expr.filter) {
      this.extractTablesFromExpression(expr.filter, tableNames, cteNames);
    }

    // IN/ANY の child を処理
    if (expr.child) {
      this.extractTablesFromExpression(expr.child, tableNames, cteNames);
    }
  }

  /**
   * FromTableからテーブル名を再帰的に抽出
   *
   * @param fromTable FromTable
   * @param tableNames 抽出されたテーブル名の集合
   * @param cteNames CTE名の集合（これらは実テーブルではないので除外する）
   */
  private extractTablesFromFromTable(
    fromTable: FromTable,
    tableNames: Set<string>,
    cteNames: Set<string> = new Set()
  ): void {
    switch (fromTable.type) {
      case 'BASE_TABLE':
        // 通常のテーブル参照
        if (fromTable.table_name) {
          logger.debug('[DuckDB] Extracted table name from AST', {
            raw: fromTable.table_name,
            isCTE: cteNames.has(fromTable.table_name)
          });
          // CTE名は除外する（これらは一時テーブルであり、実テーブルではない）
          if (!cteNames.has(fromTable.table_name)) {
            tableNames.add(fromTable.table_name);
          }
        }
        break;

      case 'JOIN':
      case 'CROSS_PRODUCT':
        // JOINまたはCROSS PRODUCT（カンマ区切りのテーブル）の左右を再帰的に処理
        if (fromTable.left) {
          this.extractTablesFromFromTable(fromTable.left, tableNames, cteNames);
        }
        if (fromTable.right) {
          this.extractTablesFromFromTable(fromTable.right, tableNames, cteNames);
        }
        break;

      case 'SUBQUERY':
        // サブクエリ内のテーブル参照を抽出（FROM句のderived table）
        if (fromTable.subquery?.node) {
          this.extractTablesFromQueryNode(fromTable.subquery.node, tableNames, cteNames);
        }
        break;

      default:
        // その他のタイプ（CROSS_PRODUCT、UNIONなど）は必要に応じて追加
        logger.debug('[DuckDB] Unknown FROM table type', { type: fromTable.type });
    }
  }

  /**
   * テーブル名をデータセットIDにマッピング
   * テーブル名の正規化（ハイフン ⇄ アンダースコア）を考慮
   *
   * @param tableNames テーブル名配列
   * @param datasets 利用可能なデータセット
   * @returns データセットIDの配列
   */
  private mapTablesToDatasets(
    tableNames: string[],
    datasets: Record<string, DataSet>
  ): DatasetId[] {
    const datasetIds: DatasetId[] = [];
    const availableDatasets = Object.entries(datasets).map(([id, ds]) => ({ id, name: ds.name }));

    logger.debug('[DuckDB] Mapping table names to datasets', {
      tableNames,
      availableDatasets
    });

    for (const tableName of tableNames) {
      let found = false;
      // データセット名で検索
      for (const [id, dataset] of Object.entries(datasets)) {
        // json_serialize_sqlはクォートなしのテーブル名を返すため、データセット名と直接比較
        if (dataset.name.toLowerCase() === tableName.toLowerCase()) {
          datasetIds.push(id as DatasetId);
          found = true;
          logger.debug('[DuckDB] Matched table to dataset', {
            tableName,
            datasetName: dataset.name,
            datasetId: id
          });
          break;
        }
      }
      if (!found) {
        logger.warn('[DuckDB] No dataset found for table name', {
          tableName,
          availableDatasets: availableDatasets.map(d => d.name)
        });
      }
    }

    return datasetIds;
  }

  /**
   * SQLクエリを実行する（WithoutLock版、DuckDB接続ロック取得済み前提）
   *
   * **ロックについて**：
   * DuckDB-WASMの接続（this.conn）への排他アクセスを管理するロック（this.lockPromise）。
   * 非同期処理で複数のSQL操作が同時実行されると、DuckDB内部でデータ競合や
   * メモリ破損が発生する可能性があるため、一度に1つのSQL操作のみを許可する必要がある。
   * このメソッドは、呼び出し元が既にロックを取得していることを前提とする。
   *
   * **前提条件**：
   * - DuckDB接続ロックが既に取得されていること（this.isLocked() === true）
   * - 全データセットがDuckDBに登録済みであること（ensureAllDatasetsRegistered実行済み）
   *
   * **使用場面**：
   * - executeSQL（Public版）から呼ばれる場合
   * - executeOperationWithoutLockから再帰的に呼ばれる場合（DerivedDataSet評価中）
   *
   * **注意**：
   * 外部から直接呼ぶ場合は必ずexecuteSQL（Public版）を使用すること。
   * このメソッドを直接呼ぶとロック未取得のままSQL操作を行うため、
   * データ競合やデッドロックのリスクがある。
   *
   * @param query 実行するSQLクエリ
   * @param targetDataset クエリの対象データセット
   * @param perfStart パフォーマンス計測の開始時刻
   * @param existingSchema 既存のスキーマ（DerivedDataSet再評価時にカラムIDを保持するため）
   * @returns SQL実行結果
   */
  async executeSQLWithoutLock(
    query: string,
    targetDataset: DataSet,
    perfStart: number,
    existingSchema?: DataSchema
  ): Promise<Omit<OperationResult, 'parentDatasetIds'>> {
    logger.assert(this.isLocked(), "Lock must be acquired before calling executeSQLWithoutLock");

    // トポロジカル評価とデータセット登録は呼び出し元（executeSQL）の
    // ensureAllDatasetsRegisteredで実行済み
    // ここではクエリ実行のみ行う

    // DML/DDLの禁止チェック（SELECT文のみ許可）
    await this.validateQueryIsSafe(query);

    logger.debug('[DuckDB] About to execute query', {
      query,
      targetTableName: this.quoteTableName(targetDataset.name)
    });

    // existingSchemaが渡されていない場合、targetDatasetから判定
    // DerivedDataSetの再評価時は既存のスキーマを保持してカラムIDを維持
    const schemaToUse = existingSchema ||
      (targetDataset.type === 'derived' && targetDataset.schema ? targetDataset.schema : undefined);

    try {
      // クエリ実行（既にロック取得済み）
      const result = await this.conn!.query(query);
      const operationResult = this.arrowToOperationResult(result, schemaToUse);

      const totalTime = performance.now() - perfStart;
      logger.debug('[DuckDB] Query executed', { durationMs: totalTime.toFixed(2) });

      logger.debug('[DuckDB] Query executed successfully', {
        query: query.substring(0, 200),
        rows: operationResult.rowCount,
        columns: operationResult.schema.columns.map(c => c.name),
        reusedSchema: !!existingSchema
      });

      return operationResult;
    } catch (error) {
      logger.error('[DuckDB] Query execution failed', {
        query,
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : undefined
      });
      throw new Error(`SQL execution failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * クエリ実行に必要なデータセットのみをDuckDBに登録する
   *
   * 渡されたテーブル名に対応するデータセットとその依存関係にある
   * データセットのみをトポロジカル順で処理して登録します。
   * これにより、不要なデータセットの評価を避け、パフォーマンスを向上させます。
   *
   * このメソッドはロック取得済み前提で呼び出されます。
   *
   * @param tableNames クエリが参照するテーブル名の配列
   * @param allDatasets 全データセットのマップ
   */
  private async ensureRequiredDatasetsRegisteredByTableNames(
    tableNames: string[],
    allDatasets: Record<string, DataSet>
  ): Promise<void> {
    logger.assert(this.isLocked(), "Lock must be acquired before calling ensureRequiredDatasetsRegisteredByTableNames");

    const perfStart = performance.now();

    if (tableNames.length === 0) {
      logger.warn('[DuckDB] No table names provided');
      return;
    }

    // 参照テーブルに対応するDataSetを特定
    const referencedDatasetIds = this.mapTablesToDatasets(tableNames, allDatasets);

    if (referencedDatasetIds.length === 0) {
      logger.warn('[DuckDB] No datasets found for referenced tables', {
        tableNames
      });
      return;
    }

    // 参照されたDataSetとその依存関係を収集
    const requiredDatasetIds = this.collectDependencies(referencedDatasetIds, allDatasets);
    const requiredDatasets = requiredDatasetIds.map(id => allDatasets[id]).filter(Boolean);

    logger.info('[DuckDB] Required datasets identified', {
      referenced: referencedDatasetIds,
      withDependencies: requiredDatasetIds,
      requiredNames: requiredDatasets.map(d => d.name),
      count: requiredDatasets.length,
      totalDatasets: Object.keys(allDatasets).length
    });

    // 必要なDataSetのみをトポロジカル順でソート
    const sortedDatasets = topologicalSort(requiredDatasets);

    logger.info('[DuckDB] Topological sort completed', {
      count: sortedDatasets.length,
      order: sortedDatasets.map(d => `${d.name}(${d.type})`)
    });

    let evaluatedCount = 0;

    // 依存関係順に処理
    for (const dataset of sortedDatasets) {
      if (dataset.data) {
        // 評価済み（PrimaryまたはDerived）→ そのまま登録
        await this.registerDatasetWithoutLock(dataset, true); // skipIfExists=true
        logger.debug('[DuckDB] Registered dataset', {
          name: dataset.name,
          type: dataset.type,
          rows: dataset.rowCount
        });
      } else if (dataset.type === 'derived') {
        // 未評価Derived → 評価してから登録
        try {
          logger.debug('[DuckDB] Evaluating dataset', { name: dataset.name });

          if (!dataset.operation) {
            throw new Error(`No operation defined for derived dataset: ${dataset.name}`);
          }

          // Worker内用SQL実行関数を作成
          // executeSQLWithoutLockを直接使用（新しいWorkerを起動しない）
          const sqlExecutor: SqlExecutor = async (query, target, _ds, schema) => {
            const perfStart = performance.now();
            return this.executeSQLWithoutLock(query, target, perfStart, schema);
          };

          // executeOperationWithoutLockを直接呼び出す（ロック取得済み前提）
          const result = await executeOperationWithoutLock(
            dataset.operation,
            allDatasets,
            sqlExecutor,
            dataset.parentDatasetIds[0],
            dataset.schema,
            dataset
          );

          logger.info('[DuckDB] Evaluation result', {
            name: dataset.name,
            rowCount: result.rowCount,
            hasData: !!result.data,
            columnCount: result.schema.columns.length
          });

          // 評価結果をallDatasetsに反映（キャッシュ）
          const evaluatedDataset = {
            ...dataset,
            data: result.data,
            rowCount: result.rowCount
          };
          allDatasets[dataset.id] = evaluatedDataset;

          // 評価済みデータセットをDuckDBに登録
          await this.registerDatasetWithoutLock(evaluatedDataset, false);

          logger.info('[DuckDB] Dataset evaluated and registered successfully', {
            name: dataset.name,
            rows: result.rowCount
          });

          evaluatedCount++;
        } catch (error) {
          // 評価失敗 = プロジェクトファイル破損
          logger.error('[DuckDB] Project file corruption detected', {
            datasetId: dataset.id,
            datasetName: dataset.name,
            error: error instanceof Error ? error.message : String(error)
          });

          throw new Error(
            `Project file appears to be corrupted. Failed to evaluate dataset "${dataset.name}": ${error instanceof Error ? error.message : String(error)}`
          );
        }
      }
    }

    const totalTime = performance.now() - perfStart;
    logger.info('[DuckDB] ⏱️ dataset registration and evaluation time', {
      ms: totalTime.toFixed(2),
      requiredDatasets: sortedDatasets.length,
      evaluated: evaluatedCount
    });
  }

  /**
   * 指定されたDataSetとその依存関係にある全DataSetのIDを収集
   *
   * @param datasetIds 開始DataSetのID配列
   * @param allDatasets 全データセットのマップ
   * @returns 依存関係を含む全DataSetのID配列
   */
  private collectDependencies(
    datasetIds: DatasetId[],
    allDatasets: Record<string, DataSet>
  ): DatasetId[] {
    const collected = new Set<DatasetId>(datasetIds);
    const toProcess = [...datasetIds];

    while (toProcess.length > 0) {
      const currentId = toProcess.pop()!;
      const dataset = allDatasets[currentId];

      if (!dataset) continue;

      // DerivedDataSetの場合、親DataSetを収集
      if (dataset.type === 'derived') {
        for (const parentId of dataset.parentDatasetIds) {
          if (!collected.has(parentId)) {
            collected.add(parentId);
            toProcess.push(parentId);
          }
        }
      }
    }

    return Array.from(collected);
  }

  /**
   * SQLクエリを実行する（Public版、DuckDB接続ロックを取得する）
   *
   * **ロックについて**：
   * DuckDB-WASMの接続（this.conn）への排他アクセスを管理するロック（this.lockPromise）を取得します。
   * 非同期処理で複数のSQL操作が同時実行されると、DuckDB内部でデータ競合や
   * メモリ破損が発生する可能性があるため、一度に1つのSQL操作のみを許可する必要があります。
   *
   * **処理フロー**：
   * 1. DuckDBを初期化します
   * 2. ロックを取得します（withLock）
   * 3. クエリから参照テーブル名を抽出します（extractTableNamesFromQuery）
   * 4. クエリ実行に必要なデータセットのみをDuckDBに登録します（ensureRequiredDatasetsRegisteredByTableNames）
   *    - 参照テーブルとその依存関係にあるDataSetを特定します
   *    - 必要なDataSetのみをトポロジカル順で評価・登録します
   * 5. SQLクエリを実行します（executeSQLWithoutLock）
   * 6. parentDatasetIdsを計算して結果に追加します
   *
   * **最適化**：
   * 以前は全データセットを登録していましたが、現在は必要なデータセットのみを登録します。
   * これにより、重いパラメータ推定を含むモデルがプロジェクトに存在する場合でも、
   * それらが参照されない限り評価されません。
   *
   * **使用場面**：
   * - SQL Editorからのクエリ実行
   * - executeOperation（Public版）から呼ばれる場合
   * - DerivedDataAccessor.loadData()から呼ばれる場合（evaluateDerivedDataset経由）
   *
   * **デッドロック回避**：
   * このメソッド内でDerivedDataSet評価が発生し、再帰的にSQLが実行される場合でも、
   * 内部では全てWithoutLock版を使用するためデッドロックは発生しません。
   *
   * @param query 実行するSQLクエリ
   * @param targetDataset クエリの対象データセット
   * @param allDatasets 全データセットのマップ
   * @returns SQL実行結果
   */
  async executeSQL(
    query: string,
    targetDataset: DataSet,
    allDatasets: Record<string, DataSet>,
    existingSchema?: DataSchema
  ): Promise<OperationResult> {
    const perfStart = performance.now();

    // デバッグ: 呼び出し時の状態
    logger.debug('[DuckDB] executeSQL START', {
      targetDatasetName: targetDataset.name,
      targetDatasetId: targetDataset.id,
      targetDatasetType: targetDataset.type,
      targetDatasetHasData: !!targetDataset.data,
      allDatasetsKeys: Object.keys(allDatasets),
      query: query.substring(0, 100)
    });

    // 初期化を待つ
    await this.init();

    // ロックを取得して必要なデータセットのみを登録 + SQL実行
    return this.withLock(async () => {
      // クエリから参照テーブル名を抽出（1回だけ）
      const tableNames = await this.extractTableNamesFromQuery(query);

      // クエリ実行に必要なDataSetのみをDuckDBに登録（トポロジカル評価含む）
      await this.ensureRequiredDatasetsRegisteredByTableNames(tableNames, allDatasets);

      // トポロジカル評価後、targetDatasetの最新版を取得
      const currentTargetDataset = allDatasets[targetDataset.id] || targetDataset;

      // SQL実行
      const operationResult = await this.executeSQLWithoutLock(query, currentTargetDataset, perfStart, existingSchema);

      // parentDatasetIdsを計算して結果に追加
      // テーブル名から親データセットを特定できない場合はtargetDatasetをフォールバックとして使用
      const parentDatasetIds = this.mapTablesToDatasets(tableNames, allDatasets);
      return {
        ...operationResult,
        parentDatasetIds: parentDatasetIds.length > 0 ? parentDatasetIds : [targetDataset.id]
      };
    });
  }

  /**
   * SQLクエリが安全か検証（SELECT/WITH文のみ許可）
   *
   * json_serialize_sqlでASTを取得し、ノードタイプから
   * DML/DDL操作を検出します。テーブルが存在しなくても検証可能です。
   *
   * @param query 検証対象のクエリ
   * @throws {Error} 禁止されたSQL文が検出された場合
   */
  private async validateQueryIsSafe(query: string): Promise<void> {
    if (!this.conn) {
      throw new Error('DuckDB not initialized');
    }

    // 空クエリチェック
    const trimmed = query.trim();
    if (!trimmed) {
      throw new Error('Query is empty');
    }

    try {
      // json_serialize_sqlでASTを取得
      // テーブルが存在しなくても構文解析は成功する
      const result = await this.conn.query(
        `SELECT json_serialize_sql(${this.escapeSQLString(query)}) as ast`
      );
      const rows = result.toArray() as Array<{ ast: string }>;

      if (rows.length === 0 || !rows[0].ast) {
        throw new Error('Failed to analyze query structure');
      }

      // ASTをパース
      const ast = JSON.parse(rows[0].ast) as DuckDBASTResponse;

      if (ast.error) {
        throw new Error('Query contains syntax errors');
      }

      // 複数ステートメントの禁止
      // DuckDB-WASMのquery()は複数ステートメントを渡しても最初の結果のみ返すため、
      // 2番目以降のステートメント（DROP TABLEなど）が実行されるリスクがある
      if (ast.statements.length > 1) {
        throw new Error('Multiple statements are not allowed. Please execute one query at a time.');
      }

      // 全てのステートメントのタイプをチェック
      for (const statement of ast.statements) {
        const nodeType = statement.node.type;

        // 許可されているノードタイプ:
        // - SELECT_NODE: 通常のSELECT文
        // - SET_OPERATION_NODE: UNION, INTERSECT, EXCEPT
        const allowedNodeTypes = ['SELECT_NODE', 'SET_OPERATION_NODE'];
        if (!allowedNodeTypes.includes(nodeType)) {
          throw new Error(
            `SQL operation '${nodeType}' is not allowed. ` +
            `Only SELECT queries (including UNION/INTERSECT/EXCEPT) are permitted. ` +
            `DML/DDL operations would cause unexpected side effects.`
          );
        }
      }

      logger.debug('[DuckDB] Query validation passed', {
        query: query.substring(0, 100)
      });
    } catch (error) {
      if (error instanceof Error) {
        // 既にvalidateQueryIsSafeでthrowしたエラーはそのまま再スロー
        if (error.message.includes('not allowed') || error.message.includes('Query is empty')) {
          throw error;
        }
        // json_serialize_sql自体のエラーの場合
        throw new Error(`Invalid SQL query: ${error.message}`);
      }
      throw error;
    }
  }

  /**
   * テーブル名をSQL識別子として使えるようにクォート
   */
  private quoteTableName(name: string): string {
    // ダブルクォートで囲んで任意の文字列をテーブル名として使用可能にする
    // ダブルクォート自体はエスケープ
    return `"${name.replace(/"/g, '""')}"`;
  }

  /**
   * MIDASのDataSetをArrowテーブルに変換
   */
  private datasetToArrow(dataset: DataSet): arrow.Table {
    const batches: Record<string, (number | string | boolean | Date | bigint | null)[]> = {};

    for (const column of dataset.schema.columns) {
      // データを取得（データの存在チェックは呼び出し元で実施済み）
      const columnData = dataset.data![column.id] || [];

      // データを型に応じて変換
      if (column.type === 'datetime' || column.type === 'date') {
        // datetime/date型: ISO文字列をDate objectsに変換してからArrowに渡す
        // Arrowは自動的にTimestampMillisecondとして認識する
        batches[column.name] = columnData.map(value => {
          if (value === null || value === undefined) return null;
          if (typeof value === 'string') {
            const date = new Date(value);
            return isNaN(date.getTime()) ? null : date;
          }
          if (typeof value === 'number') return new Date(value);
          return null;
        });
      } else if (column.type === 'int64') {
        // int64型: numberをBigIntに変換
        batches[column.name] = columnData.map(value => {
          if (value === null || value === undefined) return null;
          if (typeof value === 'number') return BigInt(Math.floor(value));
          if (typeof value === 'bigint') return value;
          return null;
        });
      } else {
        // その他の型（string, boolean, float64）はそのまま
        batches[column.name] = columnData;
      }
    }

    // tableFromArraysを使用してテーブルを構築
    // Date objectsはTimestampMillisecondとして自動的に推論される
    const table = arrow.tableFromArrays(batches);

    return table;
  }

  /**
   * Arrow結果をMIDAS形式（OperationResult）に変換
   * 既存のスキーマがある場合は、そのカラムIDを再利用する
   *
   * Row #列の扱い：
   * - クエリ結果にRow #列が含まれている場合、それを除外して新しいRow #列を生成
   * - DerivedDataSetは常に0始まりの連番のRow #列を持つ
   * - 元のRow #が必要な場合は、SELECT "Row #" AS source_row のようにリネームして使用
   */
  private arrowToOperationResult(arrowResult: arrow.Table, existingSchema?: DataSchema): Omit<OperationResult, 'parentDatasetIds'> {
    const columns: ColumnSchema[] = [];
    const data: { [columnId: string]: DataValue[] } = {};

    // Row #列が含まれているかチェック
    const rowIdColumnIndex = arrowResult.schema.fields.findIndex(
      field => field.name === ROW_ID_COLUMN_NAME
    );

    if (rowIdColumnIndex >= 0) {
      logger.warn('[DuckDB] Row # column found in query result - it will be excluded and regenerated', {
        message: 'Row # is a system column and will be regenerated as a 0-based sequence. ' +
                 'If you need the original row numbers, use AS clause: SELECT "Row #" AS source_row, ...'
      });
    }

    // スキーマとデータを変換
    for (let i = 0; i < arrowResult.schema.fields.length; i++) {
      const field = arrowResult.schema.fields[i];

      // Row #列はスキップ（後で新しく追加する）
      if (i === rowIdColumnIndex) {
        logger.debug('[DuckDB] Skipping Row # column from query result');
        continue;
      }

      // 既存スキーマから同じ名前のカラムIDを探す
      const existingColumn = existingSchema?.columns.find(col => col.name === field.name);
      const columnId = existingColumn
        ? existingColumn.id
        : createColumnId(`col-${crypto.randomUUID()}`);

      logger.debug(existingColumn ? '[DuckDB] Reusing column ID' : '[DuckDB] Creating new column ID', {
        name: field.name,
        id: columnId
      });

      columns.push({
        id: columnId,
        name: createColumnName(field.name),
        type: this.mapArrowTypeToMIDAS(field.type),
        nullable: field.nullable
      });

      const column = arrowResult.getChildAt(i);

      // カラムの型がTimestampまたはDateかどうかチェック
      const isTimestamp = arrow.DataType.isTimestamp(field.type);
      const isDate = arrow.DataType.isDate(field.type);
      const isInterval = arrow.DataType.isInterval(field.type);

      // Arrowの列データを正しく取得
      // toArray()はnullビットマップを無視して0などのデフォルト値を返す場合があるため、
      // column.get(i)を使って各値を個別に取得する必要がある
      const values: DataValue[] = [];
      for (let rowIdx = 0; rowIdx < arrowResult.numRows; rowIdx++) {
        let value = column?.get(rowIdx);

        // Interval型の場合、人間が読める文字列に変換
        // Arrow JSのgetInterval関数はMONTH_DAY_NANOをサポートしていないため、
        // 直接データバッファから読み取って変換する
        if (isInterval) {
          if (value === null || value === undefined) {
            values.push(null);
          } else {
            const intervalStr = this.convertIntervalToString(field.type, column, rowIdx);
            values.push(intervalStr);
          }
          continue;
        }

        // TimestampまたはDateの場合、ISO文字列に変換
        if (isTimestamp || isDate) {
          if (value === null || value === undefined) {
            values.push(null);
          } else {
            let ms: number;
            if (isDate) {
              // DATE型: Arrow JSのget()メソッドはDate型の場合、
              // 内部表現（日数/ミリ秒）に関わらずミリ秒を返す
              const numValue = typeof value === 'bigint' ? Number(value) : value;
              ms = numValue;
            } else {
              // Timestamp型: BigIntミリ秒をnumberに変換
              ms = typeof value === 'bigint' ? Number(value) : value;
            }
            values.push(asISODateTimeString(new Date(ms).toISOString()));
          }
          continue;
        }

        // BigIntをnumberに変換
        //
        // 背景：
        // - DuckDBのBIGINT型（MIDASのint64）はJavaScriptのBigIntにマッピングされる
        // - BigIntはMath関数が使えず、暗黙の型変換もできない（181n + 1 → エラー）
        // - D3.jsなどのビジュアライゼーションライブラリがBigIntをサポートしていない
        //
        // 方針：
        // - int64/float64を統一的にnumber型として扱う
        // - JavaScriptのnumberは±2^53（約9兆）まで正確なので、ほとんどのユースケースで十分
        // - IDやタイムスタンプで2^53を超える値が必要な場合は将来的に対応を検討
        if (typeof value === 'bigint') {
          value = Number(value);
        }

        // NaNをnullに正規化
        //
        // 背景：
        // - DuckDBのTRY_CAST失敗時などでJavaScriptにNaNが渡ってくる場合がある
        // - JavaScriptではnullとNaNで挙動が異なる（null + 5 = 5、NaN + 5 = NaN）
        // - データ型によってエラー値の表現が異なると、チェック時に v === null || Number.isNaN(v) が必要になり煩雑
        //
        // 方針：
        // - MIDAS全体でエラー値はnullに統一（DuckDB/SQLの思想と一致）
        // - 統計計算は既にnull/undefinedをfilterで除外しているため、暗黙の型変換（null + 5 = 5）の影響を受けない
        // チェックが v != null だけで済み、コードの保守性が向上
        if (typeof value === 'number' && Number.isNaN(value)) {
          values.push(null);
        } else {
          values.push(value);
        }
      }

      data[columnId] = values;
    }

    // 新しいRow #列を先頭に追加（0始まりの連番）
    columns.unshift({
      id: ROW_ID_COLUMN_ID,
      name: ROW_ID_COLUMN_NAME,
      type: 'int64',
      nullable: false
    });
    data[ROW_ID_COLUMN_ID] = Array.from({ length: arrowResult.numRows }, (_, i) => i);

    logger.debug('[DuckDB] Added new Row # column', {
      rowCount: arrowResult.numRows
    });

    const schema: DataSchema = { columns };

    return {
      schema,
      data,
      rowCount: arrowResult.numRows
    };
  }

  /**
   * Interval型のデータを人間が読める文字列に変換
   *
   * DuckDBのINTERVAL型はArrow形式でMONTH_DAY_NANOとして返される。
   * Arrow JSのgetInterval関数はMONTH_DAY_NANOをサポートしていないため、
   * 直接データバッファから読み取って変換する。
   *
   * MONTH_DAY_NANOの内部構造（16バイト）:
   * - bytes 0-3: months (Int32)
   * - bytes 4-7: days (Int32)
   * - bytes 8-15: nanoseconds (Int64)
   *
   * @param fieldType Arrow型情報
   * @param column Arrowカラム
   * @param rowIdx 行インデックス
   * @returns 人間が読める形式の文字列（例: "1 year 2 months 3 days 04:05:06.789"）
   */
  private convertIntervalToString(
    fieldType: arrow.DataType,
    column: arrow.Vector | null,
    rowIdx: number
  ): string {
    if (!column) {
      return '';
    }

    // Arrowカラムの内部データにアクセス
    // column.data は Data[] 型で、各チャンクのデータを保持
    const columnData = column.data[0];
    if (!columnData || !columnData.values) {
      logger.warn('[DuckDB] Interval column has no data');
      return '';
    }

    // IntervalUnit を取得
    const intervalType = fieldType as arrow.Interval;
    const unit = intervalType.unit;

    // MONTH_DAY_NANO (unit === 2) の場合、16バイト/要素
    // DAY_TIME (unit === 1) の場合、8バイト/要素（2つのInt32）
    // YEAR_MONTH (unit === 0) の場合、4バイト/要素（1つのInt32）
    if (unit === 2) {
      // MONTH_DAY_NANO: 16バイト/要素
      // valuesはInt32Arrayだが、実際には16バイト構造を保持
      const bytesPerElement = 16;
      const byteOffset = rowIdx * bytesPerElement;

      // Int32Arrayの基底バッファにアクセス
      const buffer = columnData.values.buffer;
      const dataView = new DataView(buffer, columnData.values.byteOffset + byteOffset, bytesPerElement);

      const months = dataView.getInt32(0, true); // little-endian
      const days = dataView.getInt32(4, true);
      // nanoseconds は64ビット整数（little-endian）
      const nanosLow = dataView.getUint32(8, true);
      const nanosHigh = dataView.getInt32(12, true);
      const nanoseconds = BigInt(nanosHigh) * BigInt(0x100000000) + BigInt(nanosLow);

      return this.formatInterval(months, days, nanoseconds);
    } else if (unit === 1) {
      // DAY_TIME: 8バイト/要素（days: Int32, milliseconds: Int32）
      const values = columnData.values as Int32Array;
      const idx = rowIdx * 2;
      const days = values[idx];
      const milliseconds = values[idx + 1];
      const nanoseconds = BigInt(milliseconds) * BigInt(1000000);
      return this.formatInterval(0, days, nanoseconds);
    } else {
      // YEAR_MONTH: 4バイト/要素（total_months: Int32）
      const values = columnData.values as Int32Array;
      const totalMonths = values[rowIdx];
      return this.formatInterval(totalMonths, 0, BigInt(0));
    }
  }

  /**
   * Interval値を人間が読める文字列にフォーマット
   *
   * @param months 月数
   * @param days 日数
   * @param nanoseconds ナノ秒
   * @returns フォーマットされた文字列
   */
  private formatInterval(months: number, days: number, nanoseconds: bigint): string {
    const parts: string[] = [];

    // 年と月を計算
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;

    if (years !== 0) {
      parts.push(`${years} year${Math.abs(years) !== 1 ? 's' : ''}`);
    }
    if (remainingMonths !== 0) {
      parts.push(`${remainingMonths} month${Math.abs(remainingMonths) !== 1 ? 's' : ''}`);
    }
    if (days !== 0) {
      parts.push(`${days} day${Math.abs(days) !== 1 ? 's' : ''}`);
    }

    // 時間部分を計算（ナノ秒から）
    if (nanoseconds !== BigInt(0)) {
      const totalSeconds = Number(nanoseconds / BigInt(1000000000));
      const remainingNanos = Number(nanoseconds % BigInt(1000000000));

      const hours = Math.floor(Math.abs(totalSeconds) / 3600);
      const minutes = Math.floor((Math.abs(totalSeconds) % 3600) / 60);
      const seconds = Math.abs(totalSeconds) % 60;

      // 時間文字列を構築
      let timeStr = '';
      if (hours > 0 || minutes > 0 || seconds > 0 || remainingNanos > 0) {
        const sign = nanoseconds < BigInt(0) ? '-' : '';
        timeStr = `${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

        // ミリ秒部分があれば追加
        if (remainingNanos > 0) {
          const millis = Math.floor(remainingNanos / 1000000);
          if (millis > 0) {
            timeStr += `.${millis.toString().padStart(3, '0')}`;
          }
        }
        parts.push(timeStr);
      }
    }

    // 全てゼロの場合
    if (parts.length === 0) {
      return '00:00:00';
    }

    return parts.join(' ');
  }

  /**
   * ArrowのDataTypeをMIDASの型名にマッピング
   */
  private mapArrowTypeToMIDAS(arrowType: arrow.DataType): DataType {
    // 型情報を詳細に出力（INFOレベルで確認）
    logger.info('[DuckDB] mapArrowTypeToMIDAS', {
      typeId: arrowType.typeId,
      constructor: arrowType.constructor.name,
      toString: arrowType.toString(),
      isTimestamp: arrow.DataType.isTimestamp(arrowType),
      isDate: arrow.DataType.isDate(arrowType),
      isInt: arrow.DataType.isInt(arrowType),
      isFloat: arrow.DataType.isFloat(arrowType),
      isUtf8: arrow.DataType.isUtf8(arrowType)
    });

    if (arrowType instanceof arrow.Int || arrowType instanceof arrow.Int64) {
      return 'int64';
    }
    if (arrowType instanceof arrow.Float || arrowType instanceof arrow.Float64) {
      return 'float64';
    }
    if (arrowType instanceof arrow.Bool) {
      return 'boolean';
    }
    // DataType.isTimestamp()を使用することで、すべてのTimestampサブクラスを包括的にチェック
    // (TimestampSecond, TimestampMillisecond, TimestampMicrosecond, TimestampNanosecond)
    if (arrow.DataType.isTimestamp(arrowType) || arrow.DataType.isDate(arrowType)) {
      logger.info('[DuckDB] Detected datetime type');
      return 'datetime';
    }
    // Interval型（MONTH_DAY_NANO等）は文字列として扱う
    if (arrow.DataType.isInterval(arrowType)) {
      logger.info('[DuckDB] Detected interval type');
      return 'string';
    }

    logger.info('[DuckDB] Defaulting to string type');
    return 'string';
  }
}

// NOTE: Worker化により、このファイルのDuckDBQueryEngineはWorker内で使用される。
// メインスレッドからは proxy.ts の duckdbProxy を使用すること。
//
// 互換性のため duckdbEngine もエクスポートするが、新規コードでは duckdbProxy を使用。
// 将来的に duckdbEngine は削除予定。

// グローバルインスタンス（Worker内で使用、またはProxy未使用時の互換性用）
export const duckdbEngine = new DuckDBQueryEngine();

// メインスレッド用Proxy（推奨）
export { duckdbProxy, DuckDBWorkerProxy } from './proxy';
export { DuckDBCancelledError } from './types';
